/**
 * NotesSheet.js — internal lead notes for the open thread.
 *
 * #167. Bottom sheet over Thread.js. Shows the per-(contact, channel)
 * timeline from inbox_lead_notes plus a textarea for adding a new
 * manual note. Auto-rows for transfer / assigned / done are mixed into
 * the same timeline (rendered with a small badge so the agent can tell
 * "Alice typed this" from "system noted Alice did this").
 *
 * Stays usable when the 24-hour session window is closed — notes are
 * internal, not customer-facing, so the lockdown doesn't apply.
 *
 * Loading model:
 *   - Mounts → loadNotes() runs.
 *   - Save → optimistic prepend + server POST → reload on success.
 *   - Refresh button reloads on demand (no realtime subscription).
 *   - Author can soft-delete their own rows (long-press / x button).
 */

import { html }              from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { useStore }          from '@nanostores/preact';

import { $session }          from '../stores/session.js';
import { showToast }         from '../stores/ui.js';
import { resolveAgent, $team } from '../services/team-directory.js';
import {
  $notesByKey,
  loadNotes,
  createNote,
  deleteNote,
} from '../services/lead-notes.js';

const MAX_BODY = 500;

/** Group rows by day label (Today / Yesterday / "Apr 12"). */
function _groupByDay(rows) {
  const groups = [];
  let current = null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  for (const r of rows) {
    const d = new Date(r.created_at);
    const ds = new Date(d);
    ds.setHours(0, 0, 0, 0);
    let label;
    if (ds.getTime() === today.getTime())     label = 'Today';
    else if (ds.getTime() === yesterday.getTime()) label = 'Yesterday';
    else label = ds.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (!current || current.label !== label) {
      current = { label, rows: [] };
      groups.push(current);
    }
    current.rows.push(r);
  }
  return groups;
}

function _relTime(iso) {
  const t  = new Date(iso).getTime();
  const dt = Date.now() - t;
  if (dt < 60_000)        return 'just now';
  if (dt < 3_600_000)     return `${Math.floor(dt / 60_000)}m ago`;
  if (dt < 86_400_000)    return `${Math.floor(dt / 3_600_000)}h ago`;
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function _initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+|@/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// Resolve an email through the team-directory roster so we render
// "Sambit" instead of "hello" (which is what email.split('@')[0] does
// for hello@odivend.com — reads as a greeting, not a name). Falls back
// to the full email when no name is on file, never the local-part slice.
function _displayFromEmail(email) {
  if (!email) return null;
  const resolved = resolveAgent(email);
  if (resolved?.name) {
    // Take first name only — keeps the badge tight and matches the
    // header banner's display style ("Assigned to Sambit").
    return resolved.name.split(/\s+/)[0];
  }
  return email; // full email is better than the truncated local part
}

function _kindBadge(row) {
  if (row.kind === 'transfer') {
    const from = _displayFromEmail(row.transfer_from_email) || 'Queue';
    const to   = _displayFromEmail(row.transfer_to_email)   || 'Queue';
    return html`<span class="inbox-notes-kind inbox-notes-kind-transfer">${from} → ${to}</span>`;
  }
  if (row.kind === 'assigned') {
    const to = _displayFromEmail(row.transfer_to_email);
    return html`<span class="inbox-notes-kind inbox-notes-kind-assigned">${to ? `Assigned to ${to}` : 'Assigned'}</span>`;
  }
  if (row.kind === 'done') {
    return html`<span class="inbox-notes-kind inbox-notes-kind-done">Marked done</span>`;
  }
  return null;
}

export function NotesSheet({ contactId, channelPhoneId, contactName, onClose }) {
  const session  = useStore($session);
  const notesMap = useStore($notesByKey);
  // Subscribe to $team so the badges re-render with real names once the
  // team-directory hydrate lands (otherwise we'd render "?" or full email
  // until the next render trigger).
  useStore($team);
  const key      = `${contactId}:${channelPhoneId}`;
  const entry    = notesMap[key] || { rows: [], loadedAt: 0, loading: false, error: null };

  const [draft, setDraft]   = useState('');
  const [saving, setSaving] = useState(false);

  // Initial load + reload whenever the (contact, channel) key changes.
  useEffect(() => {
    if (!contactId || !channelPhoneId) return;
    loadNotes(contactId, channelPhoneId).catch(() => {});
  }, [contactId, channelPhoneId]);

  const trimmed   = draft.trim();
  const tooLong   = trimmed.length > MAX_BODY;
  const canSave   = !saving && trimmed.length > 0 && !tooLong;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createNote({
        contactId,
        channelPhoneId,
        body:        trimmed,
        authorEmail: session?.agentEmail || null,
        authorName:  session?.agentName  || session?.agentEmail || null,
      });
      setDraft('');
    } catch (e) {
      showToast(e?.message || 'Could not save note', 2400);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadNotes(contactId, channelPhoneId).catch(() => {});
  };

  const handleDelete = async (row) => {
    // Authors only — UI gates by author_email match. The server enforces
    // the same rule independently.
    if (row.author_email !== session?.agentEmail) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      await deleteNote(row.id, contactId, channelPhoneId);
      showToast('Note deleted', 1500);
    } catch (e) {
      showToast(e?.message || 'Could not delete', 2200);
    }
  };

  const groups = _groupByDay(entry.rows || []);

  return html`
    <div class="inbox-sheet-overlay" style="display:block" onClick=${onClose}></div>
    <div
      class="inbox-bottom-sheet inbox-notes-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Internal notes"
      onClick=${(e) => e.stopPropagation()}
    >
      <div class="inbox-sheet-handle"></div>

      <div class="inbox-sheet-header inbox-notes-header">
        <h3 class="inbox-sheet-title" style="margin:0;flex:1;min-width:0">
          Notes${contactName ? ` · ${contactName}` : ''}
        </h3>
        <button
          class="inbox-header-icon-btn"
          onClick=${handleRefresh}
          title="Refresh"
          aria-label="Refresh notes"
          disabled=${entry.loading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
          </svg>
        </button>
        <button class="inbox-sheet-close" onClick=${onClose} aria-label="Close">×</button>
      </div>

      <!-- Add-note composer -->
      <div class="inbox-notes-compose">
        <textarea
          class="inbox-notes-textarea"
          placeholder="Add an internal note (only your team sees this)…"
          maxLength=${MAX_BODY}
          rows="3"
          value=${draft}
          onInput=${(e) => setDraft(e.target.value)}
          disabled=${saving}
        ></textarea>
        <div class="inbox-notes-compose-meta">
          <span class=${tooLong ? 'inbox-notes-counter is-over' : 'inbox-notes-counter'}>
            ${trimmed.length} / ${MAX_BODY}
          </span>
          <button
            class="inbox-btn inbox-btn-primary inbox-notes-save"
            onClick=${handleSave}
            disabled=${!canSave}
          >${saving ? 'Saving…' : 'Save note'}</button>
        </div>
      </div>

      <!-- Timeline -->
      <div class="inbox-notes-list">
        ${entry.loading && entry.rows.length === 0 && html`
          <div class="inbox-notes-empty">Loading notes…</div>
        `}
        ${!entry.loading && entry.rows.length === 0 && !entry.error && html`
          <div class="inbox-notes-empty">
            No notes yet. Add the first one above —<br/>
            it'll be visible to your team only.
          </div>
        `}
        ${entry.error && html`
          <div class="inbox-notes-error">${entry.error}</div>
        `}
        ${groups.map(g => html`
          <div class="inbox-notes-group" key=${g.label}>
            <div class="inbox-notes-day">${g.label}</div>
            ${g.rows.map(r => {
              const isMine = r.author_email && r.author_email === session?.agentEmail;
              const cls    = `inbox-notes-row inbox-notes-row-kind-${r.kind || 'manual'}${r._pending ? ' is-pending' : ''}`;
              return html`
                <div class=${cls} key=${r.id}>
                  <div class="inbox-notes-avatar" aria-hidden="true">
                    ${_initials(r.author_name, r.author_email)}
                  </div>
                  <div class="inbox-notes-body-col">
                    <div class="inbox-notes-meta">
                      <span class="inbox-notes-author">
                        ${r.author_name || r.author_email || 'System'}
                      </span>
                      <span class="inbox-notes-time">${_relTime(r.created_at)}</span>
                      ${isMine && r.kind === 'manual' && html`
                        <button
                          class="inbox-notes-del"
                          onClick=${() => handleDelete(r)}
                          title="Delete note"
                          aria-label="Delete note"
                        >×</button>
                      `}
                    </div>
                    ${_kindBadge(r)}
                    ${r.body && html`<div class="inbox-notes-text">${r.body}</div>`}
                  </div>
                </div>
              `;
            })}
          </div>
        `)}
      </div>
    </div>
  `;
}
